import React, { useEffect } from 'react';
import '../pages/styles/PrivacyPolicy.css';

// Renders a legal document from the shared JSON content in src/content/legal.
// The same JSON is rendered to static HTML at build time by
// scripts/prerender-legal.js, so the crawler-visible page and this
// client-rendered one can never drift apart.

function renderSpans(spans, keyPrefix) {
  return spans.map((span, i) => {
    const key = `${keyPrefix}-${i}`;
    if (typeof span === 'string') return <React.Fragment key={key}>{span}</React.Fragment>;

    const { text, bold, italic, code, href } = span;
    let node = <React.Fragment key={key}>{text}</React.Fragment>;

    if (code) node = <code key={key}>{text}</code>;
    if (bold) node = <strong key={key}>{node}</strong>;
    if (italic) node = <em key={key}>{node}</em>;
    if (href) {
      node = (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {node}
        </a>
      );
    }
    return node;
  });
}

function renderBlock(block, index) {
  const key = `block-${index}`;

  switch (block.type) {
    case 'h3':
      return <h3 key={key}>{block.text}</h3>;
    case 'p':
      return <p key={key}>{renderSpans(block.spans, key)}</p>;
    case 'ul':
      return (
        <ul key={key}>
          {block.items.map((item, i) => (
            <li key={`${key}-${i}`}>{renderSpans(item, `${key}-${i}`)}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={key}>
          {block.items.map((item, i) => (
            <li key={`${key}-${i}`}>{renderSpans(item, `${key}-${i}`)}</li>
          ))}
        </ol>
      );
    default:
      return null;
  }
}

// Keeps the tab title and description correct when the page is reached by
// client-side navigation. Direct hits are already served the correct tags in
// the prerendered HTML.
function useDocumentMeta(title, description) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta ? meta.getAttribute('content') : null;
    if (meta && description) meta.setAttribute('content', description);

    return () => {
      document.title = previousTitle;
      if (meta && previousDescription !== null) {
        meta.setAttribute('content', previousDescription);
      }
    };
  }, [title, description]);
}

function LegalDocument({ document: doc }) {
  useDocumentMeta(doc.pageTitle, doc.description);

  return (
    <div className="privacy-policy">
      <div className="privacy-policy-content">
        <h1>
          {doc.heading}
          <br />
          {doc.subheading}
        </h1>
        <h2>{doc.lastUpdated}</h2>
        {doc.blocks.map(renderBlock)}
      </div>
    </div>
  );
}

export default LegalDocument;
